import type { NewHomeRepository } from '../../domain/interfaces/NewHomeRepository';
import type {
  FloatingNavConfig,
  MobileHeaderConfig,
  DesktopHeaderConfig,
  HomeSeoContentSectionPageValue,
} from '../../domain/models/NewHomePageData';
import type { PlpRepository } from '@/features/plp/domain/interfaces/PlpRepository';
import type { ListingDesignItem, ListingFilter } from '@/features/plp/domain/models/ProductListing';
import type { ProductListing } from '@/features/plp/domain/models/ProductListing';
import type { FiltersRepository } from '@/features/filters/domain/interfaces/FiltersRepository';
import type { PlpFiltersResult } from '@/features/filters/domain/models/Filter';
import { PLP_DEFAULT_SORT, PLP_PAGE_TYPE } from '@/features/plp/constants';
import { toNewHomeSectionsAppVm, type NewHomeSectionAppVm } from '../viewModels/newHome.app.vm';

export interface NewHomePageAppResult {
  mobileHeader?: MobileHeaderConfig;
  desktopHeader?: DesktopHeaderConfig;
  floatingNav: FloatingNavConfig;
  sections: NewHomeSectionAppVm[];
  homeSeoContentSection?: HomeSeoContentSectionPageValue;
}

export interface RecommendedBrowsePageResult {
  designs: ListingDesignItem[];
  totalCount: number;
  filters: ListingFilter[];
  filtersResetUrl?: string;
}

function listingFiltersFromProductListing(listing: ProductListing): ListingFilter[] {
  return listing.filters.map((f) => ({
    category: f.category,
    items: f.items.map((i) => ({
      name: i.name,
      displayName: i.displayName,
      count: i.count,
      url: i.url,
      selected: i.selected,
    })),
  }));
}

function designsFromProductListing(listing: ProductListing): ListingDesignItem[] {
  return listing.designs.map((d) => ({
    designId: d.designId,
    designName: d.designName,
    imageUrl: d.imageUrl,
    productPageUrl: d.productPageUrl,
    price: d.price,
    ...(d.discountedPrice != null ? { discountedPrice: d.discountedPrice } : {}),
    ...(d.discountPercent != null ? { discountPercent: d.discountPercent } : {}),
    ...(d.discountOnDiamondPriceValue != null
      ? { discountOnDiamondPriceValue: d.discountOnDiamondPriceValue }
      : {}),
    ...(d.discountName != null ? { discountName: d.discountName } : {}),
    ...(d.discountValue != null ? { discountValue: d.discountValue } : {}),
    ...(d.customizationId != null ? { customizationId: d.customizationId } : {}),
    ...(d.videoUrl != null ? { videoUrl: d.videoUrl } : {}),
    ...(d.videoPosterUrl != null ? { videoPosterUrl: d.videoPosterUrl } : {}),
    ...(d.hoverImageUrl != null ? { hoverImageUrl: d.hoverImageUrl } : {}),
    ...(d.displayMetal != null ? { displayMetal: d.displayMetal } : {}),
    ...(d.metalWeight != null ? { metalWeight: d.metalWeight } : {}),
    ...(d.goldPurity != null ? { goldPurity: d.goldPurity } : {}),
    ...(d.solitaireTotalCarat != null
      ? { solitaireTotalCarat: d.solitaireTotalCarat }
      : {}),
    ...(d.diamondCarat != null ? { diamondCarat: d.diamondCarat } : {}),
    ...(d.carouselMedia != null ? { carouselMedia: d.carouselMedia } : {}),
    ...(d.hasSimilarDesigns != null ? { hasSimilarDesigns: d.hasSimilarDesigns } : {}),
  }));
}

export type NewHomeService = ReturnType<typeof createNewHomeService>;

export function createNewHomeService(
  repo: NewHomeRepository,
  plpRepo?: PlpRepository,
  filtersRepo?: FiltersRepository
) {
  return {
    getPageDataSync(): NewHomePageAppResult {
      const pageData = repo.fetchPageDataSync();
      return {
        mobileHeader: pageData.mobileHeader,
        desktopHeader: pageData.desktopHeader,
        floatingNav: pageData.floatingNav,
        sections: toNewHomeSectionsAppVm(pageData.sections),
        homeSeoContentSection: pageData.homeSeoContentSection,
      };
    },

    async getPageData(): Promise<NewHomePageAppResult> {
      const pageData = await repo.fetchPageData();
      return {
        mobileHeader: pageData.mobileHeader,
        desktopHeader: pageData.desktopHeader,
        floatingNav: pageData.floatingNav,
        sections: toNewHomeSectionsAppVm(pageData.sections),
        homeSeoContentSection: pageData.homeSeoContentSection,
      };
    },

    async getRecommendedProducts(
      tags: string,
      pageNumber: number,
      options?: { isFiltered?: boolean; signal?: AbortSignal }
    ): Promise<RecommendedBrowsePageResult> {
      if (!plpRepo) {
        return { designs: [], totalCount: 0, filters: [] };
      }
      try {
        const fetchOpts = options?.signal ? { signal: options.signal } : undefined;
        const listing = await plpRepo.fetchListing(
          {
            tags,
            pageNumber,
            sortBy: PLP_DEFAULT_SORT,
            page_type: PLP_PAGE_TYPE.HOME,
          },
          fetchOpts
        );
        return {
          designs: designsFromProductListing(listing),
          totalCount: listing.totalCount,
          filters: listingFiltersFromProductListing(listing),
          ...(listing.filtersResetUrl ? { filtersResetUrl: listing.filtersResetUrl } : {}),
        };
      } catch (error) {
        console.error('Error fetching recommended products:', error);
        return { designs: [], totalCount: 0, filters: [] };
      }
    },

    async fetchBrowseFilterPreview(params: {
      tags: string;
      tags_to_sort_filters?: string;
    }): Promise<PlpFiltersResult> {
      if (!filtersRepo) return { filters: [] };
      try {
        return await filtersRepo.fetchFilters({
          page_type: PLP_PAGE_TYPE.HOME,
          tags: params.tags,
          tags_to_sort_filters: params.tags_to_sort_filters ?? params.tags,
          is_for_filter_change: true,
        });
      } catch (error) {
        console.error('Error fetching browse filter preview:', error);
        return { filters: [] };
      }
    },
  };
}
